import React from 'react'
import {Box, Flex} from '../../../utils/chakraMonomorphic'

import {useRequiredAuth} from '../../../stores/AuthStore'
import {useIsCampMinderEmployee} from '../../../hooks/useIsCampMinderEmployee'
import {useFeatureFlag} from '../../../hooks/useFeatureFlag'
import {useAlbumTagStats} from '../../../hooks/useAlbumTagStats'
import {simplePluralizeWord} from '../../../utils'
import {ResourceID} from '../../../types'

// ConfigCat release flag gating the album tag-stats line (default off).
export const ALBUM_TAG_STATS_FLAG = 'albumTaggingStatsEnabled'

const Divider: React.FC = () => (
  <Box mx={2} display='inline-block'>
    |
  </Box>
)

interface Props {
  albumId: ResourceID
}

/**
 * CampMinder-employee-only, ConfigCat-gated line (shown under the album
 * date / photo-count / published-date row) with the album's facial-recognition
 * and manual person-tag counts, scoped to published photos.
 *
 * Renders nothing unless the viewer is a CampMinder employee AND the release
 * flag is on AND the fetch resolved. The employee check here is a best-effort
 * visibility gate; the photos endpoint independently enforces the authoritative
 * `campminderstaff` gate (403 to non-staff), so nothing can leak even if this
 * affordance were shown.
 */
export const AlbumTagStats: React.FC<Props> = ({albumId}) => {
  const {client} = useRequiredAuth()
  const isEmployee = useIsCampMinderEmployee()
  const {value: flagEnabled} = useFeatureFlag(ALBUM_TAG_STATS_FLAG, false)

  const enabled = isEmployee && flagEnabled

  const {status, data} = useAlbumTagStats({
    clientId: client.ID,
    albumId,
    enabled,
  })

  if (!enabled || status !== 'resolved' || !data) return null

  const peopleLabel = `${data.uniquePersonCount} ${
    data.uniquePersonCount === 1 ? 'person' : 'people'
  }`

  return (
    <Flex data-testid='album-tag-stats' pt={1}>
      <Box>
        {simplePluralizeWord('facial recognition tag', data.frTagCount)} across{' '}
        {peopleLabel}
      </Box>
      <Divider />
      <Box>{simplePluralizeWord('manual tag', data.manualTagCount)}</Box>
    </Flex>
  )
}
